/**
 * SERVICIO DE LICENCIAS CON SUPABASE
 * 
 * Este módulo proporciona operaciones para gestionar licencias de tests
 * Incluye funcionalidades para consultar, actualizar y descontar licencias.
 * 
 * Características principales:
 * - Consulta de licencias disponibles
 * - Actualización de licencias tras compra
 * - Descuento de licencias tras asignación de tests
 * - Manejo de errores
 */

// URLs base para los endpoints REST de Supabase
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * Servicio de licencias que maneja todas las operaciones relacionadas
 * con la gestión de licencias de tests
 */
const licenciaService = {
    /**
     * Obtiene el token de autenticación desde localStorage
     * @returns {string|null} Token de acceso o null si no existe
     */
    getAuthToken() {
        return localStorage.getItem('access_token');
    },

    /**
     * Obtiene los headers necesarios para las peticiones autenticadas
     * @returns {Object} Headers con autenticación
     */
    getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`
        };
    },

    /**
     * Obtiene el ID de la empresa del usuario actual
     * @returns {string|null} ID de la empresa o null si no existe
     */
    getCurrentCompanyId() {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return null;

            const user = JSON.parse(userStr);
            return user.companyData?.id_empresa || null;
        } catch (error) {
            console.error('Error al obtener ID de empresa:', error);
            return null;
        }
    },

    /**
     * Obtiene las licencias de una empresa específica
     * 
     * @param {string} idEmpresa - ID de la empresa (opcional, usa la empresa actual si no se proporciona)
     * @returns {Promise<Object>} Objeto con información de licencias
     * @throws {Object} Error con mensaje descriptivo si falla la consulta
     * 
     * @example
     * const licencias = await licenciaService.getLicencias();
     * // Retorna: { licencias_compradas: 100, licencias_usadas: 25, licencias_disponibles: 75 }
     */
    async getLicencias(idEmpresa = null) {
        try {
            const empresaId = idEmpresa || this.getCurrentCompanyId();
            
            if (!empresaId) {
                throw { message: 'No se pudo obtener el ID de la empresa' };
            }

            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}&select=licencias_compradas,licencias_usadas,licencias_disponibles`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: this.getAuthHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al obtener licencias' };
            }

            const data = await response.json();
            
            if (data.length === 0) {
                throw { message: 'No se encontró información de la empresa' };
            }

            return data[0];
        } catch (error) {
            console.error('Error al obtener licencias:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Verifica si hay suficientes licencias disponibles
     * 
     * @param {number} cantidadRequerida - Cantidad de licencias necesarias
     * @param {string} idEmpresa - ID de la empresa (opcional)
     * @returns {Promise<Object>} Objeto con resultado de la verificación
     * @throws {Object} Error con mensaje descriptivo si falla la verificación
     * 
     * @example
     * const resultado = await licenciaService.verificarLicenciasDisponibles(5);
     * // Retorna: { disponible: true, licencias_disponibles: 75, requeridas: 5 }
     */
    async verificarLicenciasDisponibles(cantidadRequerida, idEmpresa = null) {
        try {
            const licencias = await this.getLicencias(idEmpresa);
            
            // Si licencias_disponibles es null, calcularlo
            const disponibles = licencias.licencias_disponibles !== null 
                ? licencias.licencias_disponibles 
                : (licencias.licencias_compradas - licencias.licencias_usadas);

            return {
                disponible: disponibles >= cantidadRequerida,
                licencias_disponibles: disponibles,
                licencias_compradas: licencias.licencias_compradas,
                licencias_usadas: licencias.licencias_usadas,
                requeridas: cantidadRequerida,
                faltantes: disponibles < cantidadRequerida ? cantidadRequerida - disponibles : 0
            };
        } catch (error) {
            console.error('Error al verificar licencias:', error);
            throw error.message ? error : { message: 'Error al verificar licencias' };
        }
    },

    /**
     * Descuenta licencias después de asignar tests exitosamente
     * 
     * @param {number} cantidad - Cantidad de licencias a descontar
     * @param {string} idEmpresa - ID de la empresa (opcional)
     * @returns {Promise<Object>} Información actualizada de licencias
     * @throws {Object} Error con mensaje descriptivo si falla la actualización
     * 
     * @example
     * const resultado = await licenciaService.descontarLicencias(3);
     */
    async descontarLicencias(cantidad, idEmpresa = null) {
        try {
            const empresaId = idEmpresa || this.getCurrentCompanyId();
            
            if (!empresaId) {
                throw { message: 'No se pudo obtener el ID de la empresa' };
            }

            if (cantidad <= 0) {
                throw { message: 'La cantidad a descontar debe ser mayor a 0' };
            }

            // Primero obtenemos las licencias actuales
            const licenciasActuales = await this.getLicencias(empresaId);
            
            const nuevasLicenciasUsadas = licenciasActuales.licencias_usadas + cantidad;
            const nuevasLicenciasDisponibles = licenciasActuales.licencias_compradas - nuevasLicenciasUsadas;

            // Verificamos que no se exceda
            if (nuevasLicenciasDisponibles < 0) {
                throw { message: 'No hay suficientes licencias disponibles' };
            }

            // Actualizamos en la base de datos
            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';

            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: headers,
                body: JSON.stringify({
                    licencias_usadas: nuevasLicenciasUsadas,
                    licencias_disponibles: nuevasLicenciasDisponibles
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al actualizar licencias' };
            }

            const data = await response.json();
            
            // Actualizamos el localStorage con los nuevos valores
            this.actualizarLicenciasEnLocalStorage({
                licencias_compradas: licenciasActuales.licencias_compradas,
                licencias_usadas: nuevasLicenciasUsadas,
                licencias_disponibles: nuevasLicenciasDisponibles
            });

            return data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error al descontar licencias:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Agrega licencias después de una compra exitosa
     * 
     * @param {number} cantidad - Cantidad de licencias a agregar
     * @param {string} idEmpresa - ID de la empresa (opcional)
     * @returns {Promise<Object>} Información actualizada de licencias
     * @throws {Object} Error con mensaje descriptivo si falla la actualización
     * 
     * @example
     * const resultado = await licenciaService.agregarLicencias(50);
     */
    async agregarLicencias(cantidad, idEmpresa = null) {
        try {
            const empresaId = idEmpresa || this.getCurrentCompanyId();
            
            if (!empresaId) {
                throw { message: 'No se pudo obtener el ID de la empresa' };
            }

            if (cantidad <= 0) {
                throw { message: 'La cantidad a agregar debe ser mayor a 0' };
            }

            // Primero obtenemos las licencias actuales
            const licenciasActuales = await this.getLicencias(empresaId);
            
            const nuevasLicenciasCompradas = licenciasActuales.licencias_compradas + cantidad;
            const nuevasLicenciasDisponibles = nuevasLicenciasCompradas - licenciasActuales.licencias_usadas;

            // Actualizamos en la base de datos
            const headers = this.getAuthHeaders();
            headers['Prefer'] = 'return=representation';

            const url = `${SUPABASE_URL}/rest/v1/company?id_empresa=eq.${empresaId}`;
            const response = await fetch(url, {
                method: 'PATCH',
                headers: headers,
                body: JSON.stringify({
                    licencias_compradas: nuevasLicenciasCompradas,
                    licencias_disponibles: nuevasLicenciasDisponibles
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw { message: errorData.message || 'Error al agregar licencias' };
            }

            const data = await response.json();
            
            // Actualizamos el localStorage con los nuevos valores
            this.actualizarLicenciasEnLocalStorage({
                licencias_compradas: nuevasLicenciasCompradas,
                licencias_usadas: licenciasActuales.licencias_usadas,
                licencias_disponibles: nuevasLicenciasDisponibles
            });

            return data.length > 0 ? data[0] : null;
        } catch (error) {
            console.error('Error al agregar licencias:', error);
            throw error.message ? error : { message: 'Error en el servidor' };
        }
    },

    /**
     * Actualiza la información de licencias en el localStorage
     * @param {Object} licencias - Objeto con información de licencias
     */
    actualizarLicenciasEnLocalStorage(licencias) {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return;

            const user = JSON.parse(userStr);
            if (user.companyData) {
                user.companyData.licencias_compradas = licencias.licencias_compradas;
                user.companyData.licencias_usadas = licencias.licencias_usadas;
                user.companyData.licencias_disponibles = licencias.licencias_disponibles;
                
                localStorage.setItem('user', JSON.stringify(user));
                
                // Disparar evento personalizado para notificar cambios
                window.dispatchEvent(new CustomEvent('licenciasActualizadas', { 
                    detail: licencias 
                }));
            }
        } catch (error) {
            console.error('Error al actualizar licencias en localStorage:', error);
        }
    },

    /**
     * Obtiene las licencias desde el localStorage (método rápido sin petición al servidor)
     * @returns {Object|null} Información de licencias o null si no existe
     */
    getLicenciasFromLocalStorage() {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return null;

            const user = JSON.parse(userStr);
            if (user.companyData) {
                return {
                    licencias_compradas: user.companyData.licencias_compradas || 0,
                    licencias_usadas: user.companyData.licencias_usadas || 0,
                    licencias_disponibles: user.companyData.licencias_disponibles !== undefined 
                        ? user.companyData.licencias_disponibles 
                        : (user.companyData.licencias_compradas || 0) - (user.companyData.licencias_usadas || 0)
                };
            }
            return null;
        } catch (error) {
            console.error('Error al obtener licencias del localStorage:', error);
            return null;
        }
    }
};

export default licenciaService;